import React, { useEffect, useState, useRef, useCallback } from 'react';
import Dialog from '../../common/components/Dialog';
import Button from '../../components/buttons/SimpleButton/Button';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import Icon from '../../common/components/Icon';
import NewsletterBox from '../../home/sections/NewsletterBox';
import { saveOnboardingFinished } from './OnboardingService';
import { subscribeToNewsletter } from '../../newsletter/services/NewsletterService';

interface OnboardingModalProps {
  isOnboardingOpen: boolean;
  setIsOnboardingOpen: (isOnboardingOpen: boolean) => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOnboardingOpen, setIsOnboardingOpen }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const splideRef = useRef<any>(null);
  const splideContainerRef = useRef<HTMLDivElement>(null); // Reference to the splide container

  // Newsletter lists
  const newsletters = window.NewsletterLists.lists?.filter((item, index) => {
    const hasPreviewOrImage = item.preview_url || item.image_url;
    const isPremium = item.is_premium;
    const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
    const isLocked = item.locked;

    return item.mail_type_category_id === 1 && hasPreviewOrImage && !hasVariants && !isLocked && !isPremium;
  });

  const closeModal = () => {
    setIsOnboardingOpen(false);
    document.documentElement.classList.remove('no-scroll');
  };

  const myAccountSlug = window.myAccountPageSlug ?? '/my-account';
  const onboardingFields = window.onboardingFields;
  const headerImage = onboardingFields?.header_image;
  const headerImageDark = onboardingFields?.header_image_dark;
  const firstSlide = onboardingFields?.first_page;
  const secondSlide = onboardingFields?.second_page;
  const thirdSlide = onboardingFields?.third_page;

  // CZ 'newsletter-offers' auto-subscribe
  const autoSubscribeToNewsletter = useCallback(async () => {
    const newsletterCode = 'newsletter-offers';
    const newsletterExists = newsletters.some((newsletter) => newsletter.code === newsletterCode);

    if (newsletterExists) {
      try {
        await subscribeToNewsletter(newsletterCode);
      } catch (error) {
        console.error('Error auto-subscribing to newsletter:', error);
      }
    }
  }, [newsletters]);

  // Save onboarding finished
  const finishOnboarding = async () => {
    try {
      await saveOnboardingFinished(window.crm_user?.user.id ?? null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (currentSlideIndex === 2) {
      if (window.country.toLowerCase() === 'cz') {
        autoSubscribeToNewsletter();
      }

      finishOnboarding();
    }
  }, [autoSubscribeToNewsletter, currentSlideIndex]);

  if (!onboardingFields) {
    return null;
  }

  return (
    <Dialog isOpen={isOnboardingOpen} onClose={closeModal}>
      <div className="modal onboarding-modal" ref={splideContainerRef}>
        <div className="onboarding-modal__header-image-wrapper">
          {currentSlideIndex === 1 ? (
            <div className="onboarding-modal__header-image-wrapper onboarding-modal__header-image-wrapper--second-page">
              <Icon name="fsquare" size="xlarge" />
            </div>
          ) : (
            <div className="onboarding-modal__header-image-wrapper">
              {headerImage && <img className="onboarding-modal__header-image" src={headerImage} alt="" />}
              {headerImageDark && <img className="onboarding-modal__header-image--dark" src={headerImageDark} alt="" />}
            </div>
          )}
        </div>

        <div className="onboarding-modal__content-wrapper">
          <Splide
            options={{
              type: 'slide',
              perPage: 1,
              pagination: false,
              arrows: false,
              drag: true,
              autoHeight: true,
            }}
            hasTrack={false}
            aria-label="Onboarding modal"
            onMove={(splide, newIndex) => setCurrentSlideIndex(newIndex)}
            ref={splideRef}
          >
            <SplideTrack>
              {firstSlide && (
                <SplideSlide>
                  <div className="onboarding-modal__content onboarding-modal__content--0">
                    {firstSlide.title && <h3 className="" dangerouslySetInnerHTML={{ __html: firstSlide.title }} />}
                    {firstSlide.description && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: firstSlide.description,
                        }}
                      />
                    )}
                  </div>
                </SplideSlide>
              )}

              {secondSlide && (
                <SplideSlide>
                  <div className="onboarding-modal__content onboarding-modal__content--1">
                    {secondSlide.title && <h3 className="" dangerouslySetInnerHTML={{ __html: secondSlide.title }} />}
                    {secondSlide.description && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: secondSlide.description,
                        }}
                      />
                    )}

                    <div className="onboarding-modal__newsletter-list">
                      <NewsletterBox
                        isInOnboarding
                        selectedNewsletters={newsletters.map((newsletters) => newsletters.code)}
                      />
                    </div>
                  </div>
                </SplideSlide>
              )}

              {thirdSlide && (
                <SplideSlide>
                  <div className="onboarding-modal__content onboarding-modal__content--2">
                    {thirdSlide.title && <h3 className="" dangerouslySetInnerHTML={{ __html: thirdSlide.title }} />}
                    {thirdSlide.description && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: thirdSlide.description,
                        }}
                      />
                    )}

                    <div className="onboarding-modal__navigation">
                      {thirdSlide.home_button_label && (
                        <Button
                          label={thirdSlide.home_button_label}
                          type="primary"
                          size="medium"
                          className="onboarding-modal__navigation-button onboarding-modal__navigation-button--home"
                          href="/"
                        />
                      )}
                      <a
                        href={thirdSlide.my_account_button_link ?? myAccountSlug}
                        className="onboarding-modal__navigation-button onboarding-modal__navigation-button--my-account link-underline-14"
                      >
                        {thirdSlide.my_account_button_label}
                      </a>
                    </div>
                  </div>
                </SplideSlide>
              )}
            </SplideTrack>
          </Splide>
        </div>

        <div className="onboarding-modal__bottom">
          <button
            className="onboarding-modal__arrow onboarding-modal__arrow--prev is-hidden"
            type="button"
            aria-label="Previous slide"
            onClick={() => splideRef.current?.go('<')}
          >
            <Icon name="chevron-left" size="small" />
          </button>

          <div className="onboarding-modal__pagination">
            {[firstSlide, secondSlide, thirdSlide].map((slide, index) => (
              <span
                key={index}
                className={`onboarding-modal__pagination-dot ${currentSlideIndex === index ? 'is-active' : ''}`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>

          <button
            className={`onboarding-modal__arrow onboarding-modal__arrow--next ${currentSlideIndex === 2 ? 'is-hidden' : ''}`}
            type="button"
            aria-label="Next slide"
            onClick={() => splideRef.current?.go('>')}
          >
            <Icon name="arrow-right" size="small" />
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default OnboardingModal;
